import { useCallback, useEffect, useState } from 'react'
import Card from '@/components/ui/Card'
import { useAuthContext } from '@/lib/auth-context'
import {
  getLockerRoomDonationShare,
  updateLockerRoomDonationShare,
} from '@/lib/coreApi'

type ShareState = {
  code: string
  status: string
  url: string
  loading: boolean
  error: string
  message: string
}

const INITIAL: ShareState = {
  code: '',
  status: '',
  url: '',
  loading: false,
  error: '',
  message: '',
}

export default function CoachShareLinks() {
  const { session } = useAuthContext() as any
  const [state, setState] = useState<ShareState>(INITIAL)

  const hasLink = Boolean(state.url && state.status === 'active')

  // ---- load existing link on mount (with retry for cold starts) ----
  const loadLink = useCallback(async (attempt = 0) => {
    if (!session?.access_token) return
    setState((prev) => ({ ...prev, loading: true, error: '' }))
    try {
      const res = await getLockerRoomDonationShare()
      const d = res?.data || ({} as Record<string, any>)
      // Ensure URL is absolute (backend may return relative if SITE_URL not set)
      let url = d.url || ''
      if (url && !url.startsWith('http')) {
        url = `${window.location.origin}${url}`
      }
      setState({
        code: d.code || '',
        status: d.status || '',
        url,
        loading: false,
        error: '',
        message: '',
      })
    } catch (err: any) {
      // Retry once on network errors (Render cold start)
      if (attempt === 0 && (err?.message === 'Failed to fetch' || err?.message === 'Load failed')) {
        setTimeout(() => loadLink(1), 2000)
        return
      }
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'Unable to load share link',
      }))
    }
  }, [session?.access_token])

  useEffect(() => {
    loadLink()
  }, [loadLink])

  // ---- create or rotate ----
  const handleCreate = useCallback(async () => {
    if (!session?.access_token) {
      setState((prev) => ({ ...prev, error: 'Please sign in again.' }))
      return
    }
    setState((prev) => ({ ...prev, loading: true, error: '', message: '' }))
    try {
      const rotate = Boolean(state.code && state.status === 'active')
      const res = await updateLockerRoomDonationShare({ rotate })
      const d = res?.data || ({} as Record<string, any>)
      let url = d.url || ''
      if (url && !url.startsWith('http')) url = `${window.location.origin}${url}`
      setState({
        code: d.code || '',
        status: d.status || 'active',
        url,
        loading: false,
        error: '',
        message: '',
      })
      if (url) {
        try {
          await navigator.clipboard?.writeText?.(url)
          setState((prev) => ({
            ...prev,
            message: rotate
              ? 'Link rotated and copied to clipboard!'
              : 'Share link created and copied to clipboard!',
          }))
        } catch {
          setState((prev) => ({
            ...prev,
            message: rotate ? 'Link rotated.' : 'Share link created. Copy it below.',
          }))
        }
      }
    } catch (err: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'Unable to create share link',
      }))
    }
  }, [session?.access_token, state.code, state.status])

  // ---- copy ----
  const handleCopy = useCallback(async () => {
    if (!state.url) return
    try {
      await navigator.clipboard?.writeText?.(state.url)
      setState((prev) => ({ ...prev, message: 'Link copied to clipboard!', error: '' }))
    } catch {
      window.prompt('Copy this share link', state.url)
      setState((prev) => ({ ...prev, message: 'Link ready to copy.' }))
    }
  }, [state.url])

  // ---- revoke ----
  const handleRevoke = useCallback(async () => {
    if (!state.code || !session?.access_token) return
    const confirmed = window.confirm(
      'Revoke this share link? Anyone with the link will no longer be able to use it.',
    )
    if (!confirmed) return
    setState((prev) => ({ ...prev, loading: true, error: '', message: '' }))
    try {
      await updateLockerRoomDonationShare({ archive: true })
      setState({
        code: '',
        status: 'revoked',
        url: '',
        loading: false,
        error: '',
        message: 'Share link revoked. Generate a new one when you are ready.',
      })
    } catch (err: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'Unable to revoke share link',
      }))
    }
  }, [session?.access_token, state.code])

  return (
    <Card className="space-y-3">
      <header>
        <p className="text-xs uppercase tracking-wide text-stone-500">Share Links</p>
        <h3 className="text-base font-semibold text-stone-900">Share your locker room</h3>
        <p className="text-xs text-stone-600">
          Generate a link you can share with donors and supporters. Rotate or revoke anytime.
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn"
          onClick={handleCreate}
          disabled={state.loading}
        >
          {state.loading ? 'Working\u2026' : hasLink ? 'Rotate Link' : 'Generate Link'}
        </button>
        <button
          type="button"
          className="btn bg-white text-cocoa hover:bg-shell"
          onClick={handleCopy}
          disabled={!hasLink}
        >
          Copy Link
        </button>
        <button
          type="button"
          className="rounded-lg border border-brick px-3 py-2 text-sm font-semibold text-brick shadow-sm hover:bg-brick/10"
          onClick={handleRevoke}
          disabled={!hasLink || state.loading}
        >
          Revoke Link
        </button>
      </div>

      <div className="rounded-xl border border-sand bg-stone-50 p-3 text-sm">
        <p className="text-xs font-semibold text-stone-700">Current share link</p>
        <p className="mt-1 break-all font-mono text-xs text-stone-900">
          {hasLink ? state.url : 'Your link will appear here after you generate it.'}
        </p>
        {state.code && state.status === 'active' ? (
          <p className="mt-2 text-xs text-stone-500">
            Share this URL directly or include the code{' '}
            <span className="font-semibold">{state.code}</span> in emails.
          </p>
        ) : null}
      </div>

      {state.message ? <p className="text-xs text-emerald-700">{state.message}</p> : null}
      {state.error ? <p className="text-xs text-brick">{state.error}</p> : null}
    </Card>
  )
}
